import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart2,
  Users,
  FileCode,
  MessageSquare,
  Bot,
  Activity,
  X,
  RefreshCw,
} from 'lucide-react';
import superjson from 'superjson';
import { Button } from './Button';
import styles from './AnalyticsPanel.module.css';

interface AnalyticsPanelProps {
  onClose?: () => void;
}

interface Participant {
  userId?: number;
  displayName?: string;
  avatarUrl?: string;
  status?: string;
  isAi?: boolean;
  personaName?: string;
  personaIcon?: string;
  currentFile?: string;
}

interface AiActivityItem {
  id: string;
  status: string;
  message: string;
  currentStep?: number;
  totalSteps?: number;
  progress?: number;
  createdAt?: string;
}

interface SessionFile {
  id: string;
  fileName: string;
  filePath: string;
  language?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface TodoItem {
  id: string;
  title: string;
  isCompleted: boolean;
  generationStatus?: string;
}

interface ChatMessage {
  id: string;
  role: string;
  createdAt?: string;
  personaName?: string;
}

async function fetchSessionData<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const text = await res.text();
    try {
      return superjson.parse(text) as T;
    } catch {
      return JSON.parse(text) as T;
    }
  } catch {
    return null;
  }
}

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function getFileExtColor(ext: string): string {
  const map: Record<string, string> = {
    tsx: 'rgba(59, 130, 246, 0.15)',
    jsx: 'rgba(59, 130, 246, 0.15)',
    ts: 'rgba(59, 130, 246, 0.12)',
    js: 'rgba(234, 179, 8, 0.15)',
    css: 'rgba(168, 85, 247, 0.15)',
    html: 'rgba(249, 115, 22, 0.15)',
    json: 'rgba(34, 197, 94, 0.12)',
    py: 'rgba(59, 130, 246, 0.15)',
    vue: 'rgba(34, 197, 94, 0.15)',
    svelte: 'rgba(249, 115, 22, 0.15)',
  };
  return map[ext] || 'rgba(255,255,255,0.05)';
}

export default function AnalyticsPanel({ onClose }: AnalyticsPanelProps) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [files, setFiles] = useState<SessionFile[]>([]);
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [aiActivity, setAiActivity] = useState<AiActivityItem[]>([]);
  const [sessionName, setSessionName] = useState('');
  const [sessionCreated, setSessionCreated] = useState('');
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const loadData = useCallback(async () => {
    if (!sessionId) return;

    const [presenceData, filesData, todosData, historyData, activityData, sessionData] =
      await Promise.all([
        fetchSessionData<{ participants: Participant[] }>(
          `/_api/presence/list?sessionId=${sessionId}`,
        ),
        fetchSessionData<{ files: SessionFile[] }>(`/_api/files/list?sessionId=${sessionId}`),
        fetchSessionData<{ todos: TodoItem[] }>(`/_api/todos/list?sessionId=${sessionId}`),
        fetchSessionData<{ messages: ChatMessage[] }>(
          `/_api/ai/history?sessionId=${sessionId}&limit=100`,
        ),
        fetchSessionData<{ activities: AiActivityItem[] }>(
          `/_api/ai-activity/status?sessionId=${sessionId}`,
        ),
        fetchSessionData<{ session: { name: string; createdAt: string } }>(
          `/_api/sessions/get?id=${sessionId}`,
        ),
      ]);

    if (presenceData?.participants) setParticipants(presenceData.participants);
    if (filesData?.files) setFiles(filesData.files);
    if (todosData?.todos) setTodos(todosData.todos);
    if (historyData?.messages) setMessages(historyData.messages);
    if (activityData?.activities) setAiActivity(activityData.activities);
    if (sessionData?.session) {
      setSessionName(sessionData.session.name);
      setSessionCreated(sessionData.session.createdAt);
    }
    setLastRefresh(Date.now());
  }, [sessionId]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [loadData]);

  // Computed metrics
  const onlineUsers = participants.filter((p) => p.status === 'online' && !p.isAi);
  const aiParticipants = participants.filter((p) => p.isAi);
  const totalMessages = messages.length;
  const userMessages = messages.filter((m) => m.role === 'user').length;
  const aiMessages = messages.filter((m) => m.role === 'assistant').length;
  const completedTodos = todos.filter((t) => t.isCompleted).length;
  const processingTodos = todos.filter((t) => t.generationStatus === 'processing').length;
  const todoPercent = todos.length > 0 ? Math.round((completedTodos / todos.length) * 100) : 0;

  // File type distribution
  const fileTypes: Record<string, number> = {};
  files.forEach((f) => {
    const ext = f.fileName.split('.').pop()?.toLowerCase() || 'other';
    fileTypes[ext] = (fileTypes[ext] || 0) + 1;
  });

  // Session duration
  const durationMs = sessionCreated ? Date.now() - new Date(sessionCreated).getTime() : 0;
  const durationHrs = Math.floor(durationMs / 3600000);
  const durationMins = Math.floor((durationMs % 3600000) / 60000);
  const durationStr = durationHrs > 0 ? `${durationHrs}h ${durationMins}m` : `${durationMins}m`;

  return (
    <div className={styles.overlay}>
      <div className={styles.content}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <BarChart2 size={24} className={styles.headerIcon} />
            <div>
              <h2 className={styles.title}>
                {sessionName ? `${sessionName} — Analytics` : 'Session Analytics'}
              </h2>
              <p className={styles.subtitle}>Real-time session activity and metrics</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className={styles.refreshBadge}>
              <span className={styles.refreshDot} />
              Live — auto-refreshing
            </div>
            <Button size="sm" variant="ghost" onClick={loadData} title="Refresh now">
              <RefreshCw size={14} />
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} title="Close analytics">
              <X size={16} />
            </Button>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className={styles.scrollArea}>
          {!sessionId ? (
            <div className={styles.emptyHint}>
              <BarChart2 size={48} style={{ opacity: 0.2, margin: '0 auto 12px' }} />
              <p>Open a session to view analytics</p>
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className={styles.kpiGrid}>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>Online Users</span>
                  <span className={styles.kpiValue}>{onlineUsers.length}</span>
                  <span className={styles.kpiMeta}>
                    {aiParticipants.length} AI participant{aiParticipants.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>Messages</span>
                  <span className={styles.kpiValue}>{totalMessages}</span>
                  <span className={styles.kpiMeta}>
                    {userMessages} user / {aiMessages} AI
                  </span>
                </div>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>Files</span>
                  <span className={styles.kpiValue}>{files.length}</span>
                  <span className={styles.kpiMeta}>
                    {Object.entries(fileTypes)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 3)
                      .map(([ext, count]) => `${count} .${ext}`)
                      .join(', ')}
                  </span>
                </div>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>Todos</span>
                  <span className={styles.kpiValue}>
                    {completedTodos}/{todos.length}
                  </span>
                  <div className={styles.todoBar}>
                    <div className={styles.todoBarFill} style={{ width: `${todoPercent}%` }} />
                  </div>
                  <span className={styles.kpiMeta}>
                    {todoPercent}% complete
                    {processingTodos > 0 ? ` — ${processingTodos} processing` : ''}
                  </span>
                </div>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>Session Duration</span>
                  <span className={styles.kpiValue}>{durationStr}</span>
                  <span className={styles.kpiMeta}>
                    Started {sessionCreated ? timeAgo(sessionCreated) : '—'}
                  </span>
                </div>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>AI Activity</span>
                  <span className={styles.kpiValue}>
                    {aiActivity.filter((a) => a.status === 'processing').length > 0
                      ? `${aiActivity.filter((a) => a.status === 'processing').length} active`
                      : 'Idle'}
                  </span>
                  <span className={styles.kpiMeta}>
                    {aiActivity.filter((a) => a.status === 'completed').length} completed
                  </span>
                </div>
              </div>

              {/* Row 2: Participants + Recent Activity */}
              <div className={styles.row2}>
                <div className={styles.panel}>
                  <h3 className={styles.sectionTitle}>
                    <Users size={16} /> Participants
                  </h3>
                  {participants.length === 0 ? (
                    <div className={styles.emptyHint}>No active participants</div>
                  ) : (
                    participants.map((p, i) => (
                      <div key={p.userId || i} className={styles.participantRow}>
                        {p.avatarUrl ? (
                          <img src={p.avatarUrl} alt="" className={styles.participantAvatar} />
                        ) : (
                          <div className={styles.participantAvatarPlaceholder}>
                            {(p.displayName || p.personaName || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <span className={styles.participantName}>
                          {p.displayName || p.personaName || 'Unknown'}
                          {p.isAi && (
                            <Bot
                              size={12}
                              style={{
                                display: 'inline',
                                marginLeft: 4,
                                color: 'var(--primary)',
                                verticalAlign: 'middle',
                              }}
                            />
                          )}
                        </span>
                        {p.currentFile && <span className={styles.filePath}>{p.currentFile}</span>}
                        <span
                          className={`${styles.statusDot} ${
                            p.status === 'online'
                              ? styles.statusOnline
                              : p.status === 'away'
                                ? styles.statusAway
                                : styles.statusOffline
                          }`}
                        />
                      </div>
                    ))
                  )}
                </div>

                <div className={styles.panel}>
                  <h3 className={styles.sectionTitle}>
                    <Activity size={16} /> AI Activity
                  </h3>
                  {aiActivity.length === 0 ? (
                    <div className={styles.emptyHint}>No recent AI activity</div>
                  ) : (
                    aiActivity.slice(0, 8).map((a) => (
                      <div key={a.id} className={styles.activityItem}>
                        <span
                          className={`${styles.activityStatus} ${
                            a.status === 'processing'
                              ? styles.statusProcessing
                              : a.status === 'completed'
                                ? styles.statusCompleted
                                : styles.statusFailed
                          }`}
                        >
                          {a.status}
                        </span>
                        <span className={styles.activityMsg}>{a.message}</span>
                        <span className={styles.activityTime}>
                          {a.createdAt ? timeAgo(a.createdAt) : ''}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Row 3: Files */}
              <div className={styles.panel} style={{ marginBottom: 'var(--spacing-6)' }}>
                <h3 className={styles.sectionTitle}>
                  <FileCode size={16} /> Session Files ({files.length})
                </h3>
                {files.length === 0 ? (
                  <div className={styles.emptyHint}>No files in this session yet</div>
                ) : (
                  files.slice(0, 20).map((f) => {
                    const ext = f.fileName.split('.').pop()?.toLowerCase() || '';
                    return (
                      <div key={f.id} className={styles.fileRow}>
                        <span
                          className={styles.fileExt}
                          style={{ background: getFileExtColor(ext), color: 'var(--foreground)' }}
                        >
                          {ext}
                        </span>
                        <span className={styles.fileName}>{f.fileName}</span>
                        <span className={styles.filePath}>{f.filePath}</span>
                        <span className={styles.activityTime}>
                          {f.updatedAt ? timeAgo(f.updatedAt) : ''}
                        </span>
                      </div>
                    );
                  })
                )}
                {files.length > 20 && (
                  <div className={styles.emptyHint}>+ {files.length - 20} more files</div>
                )}
              </div>

              {/* Row 4: Recent Messages */}
              <div className={styles.panel}>
                <h3 className={styles.sectionTitle}>
                  <MessageSquare size={16} /> Recent Messages
                </h3>
                {messages.length === 0 ? (
                  <div className={styles.emptyHint}>No messages yet</div>
                ) : (
                  messages.slice(0, 10).map((m) => (
                    <div key={m.id} className={styles.activityItem}>
                      <span
                        className={styles.activityStatus}
                        style={{
                          background:
                            m.role === 'user'
                              ? 'color-mix(in srgb, var(--primary) 12%, transparent)'
                              : 'color-mix(in srgb, var(--primary) 12%, transparent)',
                          color: 'var(--primary)',
                        }}
                      >
                        {m.role === 'user' ? 'user' : m.personaName || 'AI'}
                      </span>
                      <span className={styles.activityTime}>
                        {m.createdAt ? timeAgo(m.createdAt) : ''}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
